import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FeatureToggle, UpdateFeatureToggleDto, UpdateFeatureToggleOrderDto } from '@/common/api/datasources/Api';
import { featureToggleService, useFeatureToggleQuery } from '@/common/api/services/featureToggles';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface FeatureToggleFilters {
    search: string;
    showEnabled: boolean;
    showDisabled: boolean;
}

interface FeatureToggleSorting {
    field: 'key' | 'description' | 'order' | 'updatedAt';
    direction: 'asc' | 'desc';
}

interface UseFeatureTogglesOptions {
    currentUserUUID?: string;
}

export const useFeatureToggles = (options: UseFeatureTogglesOptions = {}) => {
    const { currentUserUUID } = options;
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Стан для фільтрів та сортування
    const [filters, setFilters] = useState<FeatureToggleFilters>({
        search: '',
        showEnabled: true,
        showDisabled: true,
    });

    const [sorting, setSorting] = useState<FeatureToggleSorting>({
        field: 'order',
        direction: 'asc',
    });

    // Запит на отримання feature toggles
    const { data: toggles = [], isLoading, error, refetch } = useQuery({
        ...useFeatureToggleQuery().allFeatureTogglesQuery,
        staleTime: 1000 * 60, // Дані вважаються актуальними протягом 1 хвилини
    });

    // Стан для відстеження змін, які відбуваються
    const [updatingKeys, setUpdatingKeys] = useState<Record<string, boolean>>({});
    const [reorderingKeys, setReorderingKeys] = useState<Record<string, boolean>>({});

    // Мутація для оновлення стану feature toggle
    const updateToggleMutation = useMutation({
        mutationFn: ({ key, data }: { key: string; data: UpdateFeatureToggleDto }) => {
            // Додаємо ключ до списку тих, що оновлюються
            setUpdatingKeys(prev => ({ ...prev, [key]: true }));
            return featureToggleService.updateFeatureToggle(key, data);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['feature-toggles'] });
            toast({
                title: t('systemSettings.featureToggles.toast.updateSuccess.title'),
                description: t('systemSettings.featureToggles.toast.updateSuccess.description'),
            });
            // Прибираємо ключ зі списку тих, що оновлюються
            setUpdatingKeys(prev => {
                const updatedKeys = { ...prev };
                delete updatedKeys[variables.key];
                return updatedKeys;
            });
        },
        onError: (_, variables) => {
            toast({
                variant: 'destructive',
                title: t('systemSettings.featureToggles.toast.updateError.title'),
                description: t('systemSettings.featureToggles.toast.updateError.description'),
            });
            // Прибираємо ключ зі списку тих, що оновлюються
            setUpdatingKeys(prev => {
                const updatedKeys = { ...prev };
                delete updatedKeys[variables.key];
                return updatedKeys;
            });
            // Оновлюємо дані, щоб повернути правильний стан після помилки
            refetch();
        },
    });

    // Мутація для оновлення порядку feature toggle
    const updateToggleOrderMutation = useMutation({
        mutationFn: ({ key, data }: { key: string; data: UpdateFeatureToggleOrderDto }) => {
            // Додаємо ключ до списку тих, що змінюють порядок
            setReorderingKeys(prev => ({ ...prev, [key]: true }));
            return featureToggleService.updateFeatureToggleOrder(key, data);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['feature-toggles'] });
            toast({
                title: t('systemSettings.featureToggles.toast.orderSuccess.title'),
                description: t('systemSettings.featureToggles.toast.orderSuccess.description'),
            });
            // Прибираємо ключ зі списку тих, що змінюють порядок
            setReorderingKeys(prev => {
                const updatedKeys = { ...prev };
                delete updatedKeys[variables.key];
                return updatedKeys;
            });
        },
        onError: (_, variables) => {
            toast({
                variant: 'destructive',
                title: t('systemSettings.featureToggles.toast.orderError.title'),
                description: t('systemSettings.featureToggles.toast.orderError.description'),
            });
            // Прибираємо ключ зі списку тих, що змінюють порядок
            setReorderingKeys(prev => {
                const updatedKeys = { ...prev };
                delete updatedKeys[variables.key];
                return updatedKeys;
            });
            // Оновлюємо дані, щоб повернути правильний стан після помилки
            refetch();
        },
    });

    // Метод для зміни стану feature toggle
    const toggleFeature = (toggle: FeatureToggle) => {
        if (updatingKeys[toggle.key]) {
            return; // Якщо цей тогл вже оновлюється, не робимо нічого
        }

        updateToggleMutation.mutate({
            key: toggle.key,
            data: {
                enabled: !toggle.enabled,
                ...(currentUserUUID ? { updatedBy: currentUserUUID } : {})
            }
        });
    };

    // Метод для зміни порядку feature toggle
    const reorderToggle = (toggle: FeatureToggle, newOrder: number) => {
        if (reorderingKeys[toggle.key]) {
            return; // Якщо порядок цього тогла вже змінюється, не робимо нічого
        }

        updateToggleOrderMutation.mutate({
            key: toggle.key,
            data: {
                order: newOrder,
                ...(currentUserUUID ? { updatedBy: currentUserUUID } : {})
            }
        });
    };

    // Фільтрація та сортування
    const filteredAndSortedToggles = useMemo(() => {
        // Фільтрація
        let result = [...toggles];

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            result = result.filter(toggle =>
                toggle.key.toLowerCase().includes(searchLower) ||
                toggle.description?.toLowerCase().includes(searchLower)
            );
        }

        if (!filters.showEnabled) {
            result = result.filter(toggle => !toggle.enabled);
        }

        if (!filters.showDisabled) {
            result = result.filter(toggle => toggle.enabled);
        }

        // Сортування
        result.sort((a, b) => {
            const fieldA = a[sorting.field];
            const fieldB = b[sorting.field];

            if (fieldA === undefined || fieldB === undefined) {
                return 0;
            }

            const compareResult =
                typeof fieldA === 'string'
                    ? fieldA.localeCompare(fieldB as string)
                    : (fieldA as number) - (fieldB as number);

            return sorting.direction === 'asc' ? compareResult : -compareResult;
        });

        return result;
    }, [toggles, filters, sorting]);

    // Обробник зміни фільтрів
    const handleFilterChange = (newFilters: Partial<FeatureToggleFilters>) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    };

    // Обробник зміни сортування
    const handleSortChange = (field: FeatureToggleSorting['field']) => {
        setSorting(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    return {
        toggles: filteredAndSortedToggles,
        isLoading,
        error,
        filters,
        sorting,
        handleFilterChange,
        handleSortChange,
        toggleFeature,
        reorderToggle,
        isUpdating: (key: string) => !!updatingKeys[key],
        isReordering: (key: string) => !!reorderingKeys[key],
    };
}; 